// The user's default pillar library, in localStorage.
//
// A pillar is a NAME and a DEFINITION. The definition is not decoration: it is
// sent to the BE as `pillars_def`, where autoconfig classifies every open-text
// and Likert question against this vocabulary and uses the definition to decide
// where one pillar ends and its neighbour begins. It travels on into the report
// JSON for RAG to ground on.
//
// STORAGE SHAPE, and why reads are defensive: this key originally held a plain
// array of strings. Anyone who used the app before definitions existed still has
// that in their browser, so `readRows` accepts either shape and normalizes a
// legacy string into {name, definition: ""}. Writes are always the new shape.
//
// Per-browser, by design. It is not shared with teammates, does not follow you
// to another machine, and is lost if site data is cleared.

const DEFAULT_PILLARS = [
    {
        name: "Vision",
        definition: "What the change is for and where it is heading: clarity of the goal, how well it has been communicated, and whether people can connect it to their own role.",
    },
    {
        name: "Success Criteria",
        definition: "How the change will be judged: what good looks like, whether the measures are known and credible, and whether people would recognise success if it arrived.",
    },
    {
        name: "Culture",
        definition: "How the organisation is reacting: openness or resistance to the change, trust, morale, and whether people feel their contribution is valued through it.",
    },
    {
        name: "Leadership",
        definition: "How leaders are running the change: visibility, honesty of communication, whether feedback demonstrably changes anything, and whether staff are supported through it.",
    },
    {
        name: "Operations",
        definition: "The effect on day-to-day work: process disruption, workload, tooling, and where friction or confusion is showing up in how the work actually gets done.",
    },
    {
        name: "Capability",
        definition: "Whether people are equipped for the change: training, coaching, time to practise, and the confidence gap between those who are ready and those who are not.",
    },
];

const STORAGE_KEY = "user_default_pillars";

// Accepts both the legacy string[] and the current {name, definition}[].
const normalizeRow = (row) => {
    if (typeof row === "string") {
        return { name: row, definition: "" };
    }
    if (row && typeof row === "object" && row.name) {
        return { name: String(row.name), definition: String(row.definition || "") };
    }
    return null;
};

const readRows = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) return null;
        const parsed = JSON.parse(stored);
        if (!Array.isArray(parsed) || !parsed.length) return null;
        const rows = parsed.map(normalizeRow).filter(Boolean);
        return rows.length ? rows : null;
    } catch (error) {
        // Private mode and blocked site data make localStorage throw on ACCESS,
        // not just on write, so this guard is load-bearing.
        console.error("Error reading default pillars from localStorage:", error);
        return null;
    }
};

/** The library as {name, definition} rows. Falls back to the shipped six. */
export const getPillarLibrary = () => readRows() || DEFAULT_PILLARS.map((p) => ({ ...p }));

/** Persist the library. Accepts either shape; always stores the new one. */
export const savePillarLibrary = (rows) => {
    try {
        if (!Array.isArray(rows)) {
            console.error("Invalid pillars format. Expected an array.");
            return false;
        }
        localStorage.setItem(
            STORAGE_KEY,
            JSON.stringify(rows.map(normalizeRow).filter(Boolean)),
        );
        return true;
    } catch (error) {
        console.error("Error saving default pillars to localStorage:", error);
        return false;
    }
};

/** Restore the shipped six, definitions included. */
export const resetPillarLibrary = () => {
    const rows = DEFAULT_PILLARS.map((p) => ({ ...p }));
    savePillarLibrary(rows);
    return rows;
};

/** The shipped six, untouched by anything the user has saved. */
export const getOriginalPillarLibrary = () => DEFAULT_PILLARS.map((p) => ({ ...p }));

// --- name-only helpers -------------------------------------------------------
// Kept because callers that only need the vocabulary (e.g. InsightPage's
// pillar-name normalizer) should not have to know about definitions.

export const getDefaultPillars = () => getPillarLibrary().map((p) => p.name);

export const saveDefaultPillars = (pillars) => savePillarLibrary(pillars);

export const resetToDefaultPillars = () => resetPillarLibrary().map((p) => p.name);

export const getOriginalDefaultPillars = () => DEFAULT_PILLARS.map((p) => p.name);
